import { memo } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { formatCurrency } from "@/shared/lib/formatters";
import type { RentalResponseDTO } from "../types/rental.types";

interface RentalCancelDialogProps {
  open: boolean;
  loading: boolean;
  rental: RentalResponseDTO | null;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Confirmação de cancelamento de aluguel.
 *
 * Exibe id, cliente e valor para evitar cancelamento acidental. O
 * cancelamento dispara `POST /api/rentals/{id}/cancel` (status →
 * `CANCELED`); a operação é tratada como destrutiva pelo backend.
 *
 * @example
 * <RentalCancelDialog
 *   open={dialogMode === 'cancel'}
 *   loading={dialogLoading}
 *   rental={selected}
 *   onClose={closeDialog}
 *   onConfirm={handleCancelConfirm}
 * />
 */
export const RentalCancelDialog = memo(function RentalCancelDialog({
  open,
  loading,
  rental,
  onClose,
  onConfirm,
}: RentalCancelDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cancelar Aluguel</DialogTitle>
          <DialogDescription>
            Tem certeza que deseja cancelar este aluguel?
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="p-4 bg-destructive/10 rounded-md border border-destructive/20">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Aluguel #{rental?.id}</p>
                {rental ? (
                  <p className="text-sm text-muted-foreground">
                    Cliente: {rental.clientName}
                  </p>
                ) : null}
                {rental ? (
                  <p className="text-sm text-muted-foreground">
                    Valor: {formatCurrency(rental.totalValue)}
                  </p>
                ) : null}
                <p className="text-sm text-destructive mt-2">
                  O aluguel será marcado como cancelado e sairá do fluxo ativo.
                </p>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Voltar
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading}>
            {loading ? "Cancelando..." : "Cancelar Aluguel"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
